import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import WifiTetheringOutlinedIcon from "@mui/icons-material/WifiTetheringOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import { createDevice, getHospitals } from "../api/devicesApi";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";

const STATUS_OPTIONS = [
  { value: "ONLINE", label: "Online" },
  { value: "OFFLINE", label: "Offline" },
  { value: "MAINTENANCE", label: "Maintenance" },
];

const initialForm = {
  organization: "",
  device_model: "",
  firmware_version: "",
  status: "OFFLINE",
  last_seen_at: "",
};

export default function DeviceCreateForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [form, setForm] = React.useState(initialForm);
  const [loading, setLoading] = React.useState(false);
  const [hospitalLoading, setHospitalLoading] = React.useState(true);
  const [hospitals, setHospitals] = React.useState([]);
  const [success, setSuccess] = React.useState("");
  const [error, setError] = React.useState("");
  const [createdDevice, setCreatedDevice] = React.useState(null);

  React.useEffect(() => {
    loadHospitals();
  }, []);

  const loadHospitals = async () => {
    try {
      setHospitalLoading(true);
      setError("");

      const response = await getHospitals();

      const hospitalList = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
        ? response.results
        : [];

      setHospitals(hospitalList);
    } catch (err) {
      setError(prettyError(err));
      setHospitals([]);
    } finally {
      setHospitalLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const getHospitalValue = (hospital) => {
    if (hospital?.id !== undefined && hospital?.id !== null) return hospital.id;
    if (hospital?.organization_id !== undefined && hospital?.organization_id !== null) {
      return hospital.organization_id;
    }
    if (hospital?.hospital_id !== undefined && hospital?.hospital_id !== null) {
      return hospital.hospital_id;
    }
    if (hospital?.uuid !== undefined && hospital?.uuid !== null) {
      return hospital.uuid;
    }
    return "";
  };

  const getHospitalLabel = (hospital) => {
    return (
      hospital?.name ||
      hospital?.organization_name ||
      hospital?.hospital_name ||
      hospital?.title ||
      `Hospital ${getHospitalValue(hospital)}`
    );
  };

  const validateForm = () => {
    if (!form.organization || String(form.organization).trim() === "") {
      return "Hospital is required.";
    }
    if (!form.device_model.trim()) {
      return "Device model is required.";
    }
    if (!form.status) {
      return "Status is required.";
    }
    return "";
  };

  const buildPayload = () => {
    return {
      organization: form.organization,
      device_model: form.device_model.trim(),
      firmware_version: form.firmware_version.trim() || null,
      status: form.status,
      last_seen_at: form.last_seen_at || null,
    };
  };

  const selectedHospital = hospitals.find(
    (item) => String(getHospitalValue(item)) === String(form.organization)
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");
    setCreatedDevice(null);

    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload();
      console.log("Submitting payload:", payload);

      const response = await createDevice(payload);

      setCreatedDevice(response);
      setSuccess("Device created successfully.");
      setForm(initialForm);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setSuccess("");
    setError("");
    setCreatedDevice(null);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: Colors.bg.app,
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${Colors.border.light}`,
            backgroundColor: Colors.bg.paper,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 2, sm: 2.5, md: 3 },
              background: `linear-gradient(90deg, ${Colors.brand.primary} 0%, ${Colors.brand.primaryDark} 100%)`,
              color: "#fff",
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={2}
            >
              <Box>
                <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                  Device Registration
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Create and manage Fludee devices in a clean hospital-style form.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  icon={<MemoryOutlinedIcon />}
                  label="Fludee Device"
                  sx={{
                    color: Colors.brand.primary,
                    backgroundColor: "#ffffff",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              height: 6,
              backgroundColor: Colors.brand.accentBar,
              borderTop: `1px solid ${Colors.border.topLine}`,
            }}
          />
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${Colors.border.light}`,
                backgroundColor: Colors.bg.paper,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: Colors.text.title }}
                    >
                      Device Information
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: Colors.text.muted, mt: 0.5 }}
                    >
                      Fill all required device details. Device serial will be generated automatically.
                    </Typography>
                  </Box>

                  <Divider />

                  {success ? <Alert severity="success">{success}</Alert> : null}
                  {error ? <Alert severity="error">{error}</Alert> : null}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          select
                          label="Hospital / Organization"
                          value={form.organization}
                          onChange={handleChange("organization")}
                          required
                          disabled={hospitalLoading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ApartmentOutlinedIcon sx={{ color: Colors.text.icon }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                          helperText={
                            hospitalLoading
                              ? "Loading hospitals..."
                              : "Select the hospital for this device"
                          }
                        >
                          {hospitalLoading ? (
                            <MenuItem value="">Loading...</MenuItem>
                          ) : hospitals.length === 0 ? (
                            <MenuItem value="">No hospitals found</MenuItem>
                          ) : (
                            hospitals.map((hospital) => (
                              <MenuItem
                                key={String(getHospitalValue(hospital))}
                                value={String(getHospitalValue(hospital))}
                              >
                                {getHospitalLabel(hospital)}
                              </MenuItem>
                            ))
                          )}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Device Model"
                          placeholder="Enter device model"
                          value={form.device_model}
                          onChange={handleChange("device_model")}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MemoryOutlinedIcon sx={{ color: Colors.text.icon }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Firmware Version"
                          placeholder="e.g. 1.0.0"
                          value={form.firmware_version}
                          onChange={handleChange("firmware_version")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <UpdateOutlinedIcon sx={{ color: Colors.text.icon }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          select
                          label="Status"
                          value={form.status}
                          onChange={handleChange("status")}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <WifiTetheringOutlinedIcon sx={{ color: Colors.text.icon }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Last Seen At"
                          type="datetime-local"
                          value={form.last_seen_at}
                          onChange={handleChange("last_seen_at")}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeOutlinedIcon sx={{ color: Colors.text.icon }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Stack
                          direction={isMobile ? "column" : "row"}
                          spacing={1.5}
                          justifyContent="flex-end"
                          sx={{ mt: 1 }}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleReset}
                            startIcon={<RestartAltOutlinedIcon />}
                            disabled={loading}
                            sx={{
                              minWidth: 140,
                              borderRadius: 2.5,
                              textTransform: "none",
                              fontWeight: 700,
                              borderColor: Colors.border.input,
                              color: Colors.text.title,
                              "&:hover": {
                                borderColor: Colors.brand.primary,
                                backgroundColor: Colors.bg.hover,
                              },
                            }}
                          >
                            Reset
                          </Button>

                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={
                              loading ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <SaveOutlinedIcon />
                              )
                            }
                            disabled={loading || hospitalLoading}
                            sx={{
                              minWidth: 180,
                              borderRadius: 2.5,
                              textTransform: "none",
                              fontWeight: 700,
                              backgroundColor: Colors.brand.primary,
                              boxShadow: "none",
                              "&:hover": {
                                backgroundColor: Colors.brand.primaryDark,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {loading ? "Saving..." : "Create Device"}
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={3}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${Colors.border.light}`,
                  backgroundColor: Colors.bg.paper,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: Colors.text.title, mb: 1.5 }}
                  >
                    Selected Hospital
                  </Typography>

                  {hospitalLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ color: Colors.text.muted }}>
                        Loading hospital list...
                      </Typography>
                    </Stack>
                  ) : selectedHospital ? (
                    <Stack spacing={1}>
                      <DeviceDataRow
                        label="Hospital"
                        value={getHospitalLabel(selectedHospital)}
                      />
                      <DeviceDataRow
                        label="ID"
                        value={getHospitalValue(selectedHospital)}
                      />
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: Colors.text.muted }}>
                      No hospital selected.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {createdDevice ? (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: `1px solid ${Colors.border.light}`,
                    backgroundColor: Colors.bg.paper,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: Colors.text.title, mb: 1.5 }}
                    >
                      Created Device
                    </Typography>

                    <Stack spacing={1}>
                      <DeviceDataRow
                        label="Device ID"
                        value={createdDevice.device_id || "-"}
                      />
                      <DeviceDataRow
                        label="Serial"
                        value={createdDevice.device_serial || "-"}
                      />
                      <DeviceDataRow
                        label="Model"
                        value={createdDevice.device_model || "-"}
                      />
                      <DeviceDataRow
                        label="Status"
                        value={createdDevice.status || "-"}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function DeviceDataRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
        borderBottom: `1px solid ${Colors.border.light}`,
      }}
    >
      <Typography variant="body2" sx={{ color: Colors.text.muted }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          color: Colors.text.title,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: Colors.border.input,
    },
    "&:hover fieldset": {
      borderColor: Colors.brand.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: Colors.brand.primary,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputLabel-root": {
    color: Colors.text.muted,
  },
  "& .MuiInputBase-input": {
    color: Colors.text.body,
  },
};